
// src/pages/store/ProductDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ToastContext } from "@/components/ToastProvider.jsx";
import { useParams, useNavigate } from "react-router-dom";
import PageBackground from "@/components/PageBackground";
import MobileBackButton from "@/components/MobileBackButton";
import { useCart } from "@/contexts/CartContext.jsx";
import { findProductBySlug, findProductById, fetchStoreProductsPage } from "@/lib/storeProducts";
import aiFeatures from "@/lib/settings/ai"; // reserved for future AI-driven UI toggles
import ProductCard from "@/components/store/ProductCard";
import ProductReviews from "@/components/store/ProductReviews";

/* pick the best available image field */
function getFirstImage(p) {
  if (typeof p?.imageUrl === "string" && p.imageUrl.trim()) return p.imageUrl.trim();
  if (typeof p?.thumbnail === "string" && p.thumbnail.trim()) return p.thumbnail.trim();
  if (Array.isArray(p?.images) && p.images.length && typeof p.images[0] === "string")
    return p.images[0].trim();
  return "";
}

function fmt(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function ProductDetail() {
  const { slug } = useParams(); // may be slug (e.g. "talong") OR a doc id
  const nav = useNavigate();
  const { addItem, totalQty } = useCart();
  const { notify } = React.useContext(ToastContext);

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  // Initialize with empty string to prevent auto-filling
  const [qty, setQty] = useState('');
  const [added, setAdded] = useState(false);
  const [selectedImg, setSelectedImg] = useState("");
  const [imgOk, setImgOk] = useState(true);
  const [related, setRelated] = useState([]);
  const [relLoading, setRelLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [siblings, setSiblings] = useState({ prev: null, next: null });
  const [zoomOpen, setZoomOpen] = useState(false);
  const [cooling, setCooling] = useState(false);

  // load by slug; if not found, try as Firestore doc id
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // 1) try slug loader across all store collections
        let prod = await findProductBySlug(slug);

        // 2) if not found, try treat param as a Firestore doc id across all store collections
        if (!prod) {
          prod = await findProductById(slug);
        }

        if (!alive) return;

        if (!prod || prod.active !== true) {
          // not found or not active → bounce to /store
          nav("/store", { replace: true });
          return;
        }

        setP(prod);
        document.title = `${prod.name} • Store`;
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug, nav]);

  // Build image list and set default selected image when product loads
  const images = useMemo(() => {
    if (!p) return [];
    const arr = [];
    const add = (s) => {
      if (typeof s === "string") {
        const t = s.trim();
        if (t && !arr.includes(t)) arr.push(t);
      }
    };
    add(p.imageUrl);
    add(p.thumbnail);
    if (Array.isArray(p.images)) p.images.forEach(add);
    return arr;
  }, [p]);

  useEffect(() => {
    if (!p) return;
    const first = images[0] || "";
    setSelectedImg(first);
    setImgOk(true);
    // update recently viewed
    try {
      const key = "recentProducts:v1";
      const raw = localStorage.getItem(key);
      const arr = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
      const entry = {
        id: p.id,
        slug: p.slug || p.id,
        name: p.name,
        price: p.price,
        imageUrl: first || getFirstImage(p),
      };
      const filtered = arr.filter((x) => (x.id || x.slug) !== (entry.id || entry.slug));
      const next = [entry, ...filtered].slice(0, 12);
      localStorage.setItem(key, JSON.stringify(next));
      setRecent(next.filter((x) => (x.id || x.slug) !== (p.id || p.slug))); // exclude current
    } catch {
      /* ignore */
    }
  }, [p, images]);

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!images || images.length < 2) return;
    function onKey(e) {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const idx = images.indexOf(selectedImg);
        if (idx === -1) return;
        if (e.key === "ArrowLeft") {
          const next = idx > 0 ? images[idx - 1] : images[images.length - 1];
          setSelectedImg(next);
          setImgOk(true);
        } else {
          const next = idx < images.length - 1 ? images[idx + 1] : images[0];
          setSelectedImg(next);
          setImgOk(true);
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [images, selectedImg]);

  // For prev/next: load a small page in the same category and find neighbors around current product
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!p) {
        setSiblings({ prev: null, next: null });
        return;
      }
      const cat = Array.isArray(p.categories) && p.categories.length ? p.categories[0] : "";
      try {
        // Load 36 items and sort by createdAt desc-ish (same logic as stores fallback)
        const { items } = await fetchProductsPage({ category: cat, pageSize: 36 });
        if (!alive) return;
        const list = items;
        const idx = list.findIndex((x) => x.id === p.id);
        const prev = idx > 0 ? list[idx - 1] : null;
        const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
        setSiblings({ prev, next });
      } catch {
        if (alive) setSiblings({ prev: null, next: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, [p]);

  // Fetch related products (same first category from the same store)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!p) return;
      const cat = Array.isArray(p.categories) && p.categories.length ? p.categories[0] : "";
      if (!cat) {
        setRelated([]);
        return;
      }
      setRelLoading(true);
      try {
        // Load related products from the same store
        const { items } = await fetchStoreProductsPage({ 
          storeType: p.storeType, 
          storeId: p.storeId, 
          category: cat, 
          pageSize: 8 
        });
        if (!alive) return;
        setRelated(items.filter((x) => x.id !== p.id));
      } finally {
        if (alive) setRelLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [p]);

  if (loading)
    return (
      <PageBackground
        image="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&w=1500&q=80"
        boxed={false}
        overlayClass="bg-white/85 backdrop-blur"
        className="px-4 md:px-6"
        edgeToEdge={true}
      >
        <div className="p-2 sm:p-4">Loading…</div>
      </PageBackground>
    );

  if (!p) return null;

  const price = Number(p.price || 0);
  const stock = Number.isFinite(Number(p.stock)) ? Number(p.stock) : null;
  const canBuy = stock == null ? true : stock > 0;

  function onQtyChange(e) {
    const n = Number(e.target.value);
    const min = 0.25;
    const val = Number.isFinite(n) ? n : min;
    
    // Don't force rounding while typing - only enforce minimum
    const floored = Math.max(min, val);
    const capped = stock == null ? floored : Math.min(floored, Math.max(min, stock));
    setQty(capped);
  }

  function addToCart() {
    const raw = Number(qty || 0.25);
    const want = Math.max(0.25, Math.round(raw * 4) / 4);
    if (stock != null && want > stock) {
      notify(`Only ${stock} in stock.`, { type: "error" });
      return;
    }
    addItem({
      id: p.id,
      slug: p.slug,
      name: p.name,
      price,
      image: selectedImg || getFirstImage(p),
      qty: want,
      storeType: p.storeType,
      storeId: p.storeId,
    });
    try {
      notify(`Added to cart: ${p.name} × ${want}`, { type: "success", duration: 2000 });
    } catch {}
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
    setCooling(true);
    setTimeout(() => setCooling(false), 500);
  }

  function onQtyKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addToCart();
    }
  }

  function sanitizeNumericDot(v) {
    if (typeof v !== "string") v = String(v ?? "");
    let s = v.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
    const parts = s.split(".");
    if (parts.length > 1) s = parts[0] + "." + parts[1].slice(0, 2);
    else s = parts[0];
    return s;
  }

  return (
    <>
      <MobileBackButton to="/store" />
      <PageBackground
        image="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&w=1500&q=80"
        boxed={false}
        overlayClass="bg-white/85 backdrop-blur"
        className="px-4 md:px-6"
        edgeToEdge={true}
      >
        <div className="min-h-screen">
          <div className="p-2 sm:p-4 pb-28 sm:pb-4">
            <div className="flex items-center justify-between mb-3">
              <nav className="text-sm text-ink/60 space-x-1">
                <a href="/store" className="underline">
                  Store
                </a>
                <span>/</span>
                <span className="text-ink">{p.name}</span>
              </nav>
              <button
                className="btn btn-ghost text-sm"
                onClick={async () => {
                  const link = window.location.href;
                  try {
                    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(link);
                    else throw new Error("no clipboard");
                    notify("Link copied", { type: "success" });
                  } catch {
                    window.prompt("Copy link:", link);
                  }
                }}
                title="Copy link"
              >
                Share
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <div className="relative rounded-xl border overflow-hidden bg-gray-50">
                  {selectedImg && imgOk ? (
                    <img
                      key={selectedImg}
                      src={selectedImg}
                      alt={p.name}
                      className="w-full h-full object-contain max-h-[400px] sm:max-h-[520px]"
                      onError={() => setImgOk(false)}
                      onClick={() => setZoomOpen(true)}
                      title="Click to zoom"
                    />
                  ) : (
                    <div className="w-full h-[280px] sm:h-[320px] grid place-items-center text-ink/40">
                      No Image
                    </div>
                  )}
                  {Number(p.stock ?? 0) <= 0 && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded">
                      Out of stock
                    </div>
                  )}
                </div>

                {images.length > 1 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                    {images.map((s) => (
                      <button
                        key={s}
                        className={`h-14 w-14 sm:h-16 sm:w-16 rounded border overflow-hidden flex-shrink-0 ${
                          selectedImg === s ? "ring-2 ring-brand-500" : ""
                        }`}
                        onClick={() => {
                          setSelectedImg(s);
                          setImgOk(true);
                        }}
                        title="View image"
                      >
                        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                        <img
                          src={s}
                          alt={p.name + " thumbnail"}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          width="64"
                          height="64"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="card p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2">
                  <h1 className="text-xl sm:text-2xl font-bold">{p.name}</h1>
                  <div className="flex items-center gap-2">
                    <a
                      href="/cart"
                      className="relative inline-flex items-center justify-center h-10 w-10 sm:h-9 sm:w-9 rounded-full border hover:bg-gray-50"
                      title="Cart"
                      aria-label="Cart"
                    >
                      {/* Cart icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-5 w-5 text-ink"
                      >
                        <path d="M2.25 3a.75.75 0 000 1.5h1.386c.34 0 .64.23.72.558l2.2 8.8A1.875 1.875 0 008.37 15h8.505a1.875 1.875 0 001.814-1.35l1.64-5.415a.75.75 0 00-.72-.985H6.855l-.37-1.48A2.25 2.25 0 003.636 3H2.25z" />
                        <path d="M8.25 20.25a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17.25 20.25a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                      </svg>
                      {totalQty > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {totalQty > 99 ? "99+" : totalQty}
                        </span>
                      )}
                    </a>
                  </div>
                </div>

                {/* Unified actions */}
                <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <div className="inline-flex items-center gap-1 shrink-0">
                    <input
                      className="input h-12 sm:h-9 w-full sm:w-24 text-center text-base sm:text-sm"
                      type="number"
                      value={qty}
                      onChange={(e) => setQty(sanitizeNumericDot(e.target.value))}
                                              onBlur={(e) => {
                          const n = Number(e.target.value);
                          const min = 0.25;

                          // Allow 0 as a valid input
                          if (isNaN(n) || n < 0) {
                            setQty('');
                          } else if (n > 0 && n < min) {
                            setQty(min);
                          } else if (stock != null && n > stock) {
                            setQty(Math.max(min, stock));
                          } else {
                            setQty(n);
                          }
                        }}
                      onKeyDown={onQtyKeyDown}
                      inputMode="decimal"
                      enterKeyHint="done"
                    />
                  </div>

                  <button
                    className="btn btn-outline w-full sm:w-auto h-12 sm:h-9 p-0 grid place-items-center shrink-0"
                    onClick={addToCart}
                    disabled={!canBuy || cooling}
                    title={!canBuy ? "Out of stock" : cooling ? "Please wait…" : "Add to cart"}
                    aria-label={!canBuy ? "Out of stock" : cooling ? "Please wait…" : "Add to cart"}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 4h-2l-1 2m0 0l3 9h9l3-7H6M4 6h16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="9" cy="20" r="1.5" fill="currentColor" />
                      <circle cx="17" cy="20" r="1.5" fill="currentColor" />
                    </svg>
                  </button>

                  <button
                    className="btn btn-primary w-8 h-8 sm:w-9 sm:h-9 p-0 grid place-items-center shrink-0"
                    onClick={() => {
                      const want = Math.max(0.25, Number(qty || 0.25));
                      addItem({
                        id: p.id,
                        slug: p.slug,
                        name: p.name,
                        price,
                        image: selectedImg || getFirstImage(p),
                        qty: want,
                        storeType: p.storeType,
                        storeId: p.storeId,
                      });
                      nav("/checkout");
                    }}
                    disabled={!canBuy}
                    title={canBuy ? "Buy now" : "Out of stock"}
                    aria-label={canBuy ? "Buy now" : "Out of stock"}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                    </svg>
                  </button>
                </div>

                {/* Mini total under qty */}
                <div className="mt-1 text-sm text-ink/70">
                  Total = {Number(qty || 0).toFixed(2)} × ₱{fmt(price)} ={" "}
                  <span className="font-mono">₱{fmt(Number(qty || 0) * price)}</span>
                </div>

                <div className="mt-2 text-xl font-mono">₱{fmt(price)}</div>

                <div className="mt-1 text-sm text-ink/60">
                  {stock != null ? <>Stock: {stock}</> : <>Stock: —</>}
                  {p.sku ? (
                    <span className="ml-3 inline-flex items-center gap-2">
                      <span>SKU: {p.sku}</span>
                      <button
                        className="text-xs underline"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(p.sku);
                            notify("SKU copied", { type: "success" });
                          } catch {
                            /* ignore */
                          }
                        }}
                        title="Copy SKU"
                      >
                        Copy
                      </button>
                    </span>
                  ) : null}
                </div>

                {stock != null && stock > 0 && stock <= 5 && (
                  <div className="mt-1 text-xs text-orange-700">Only {stock} left—order soon!</div>
                )}

                {Array.isArray(p.categories) && p.categories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.categories.map((c) => (
                      <a key={c} href={`/store?cat=${encodeURIComponent(c)}`} className="badge">
                        {c}
                      </a>
                    ))}
                  </div>
                )}

                {p.description && (
                  <div className="mt-4 whitespace-pre-line">{p.description}</div>
                )}

                {added && (
                  <div className="mt-3 text-emerald-700 text-sm">Added to cart ✓</div>
                )}

                {(siblings.prev || siblings.next) && (
                  <div className="mt-6 flex items-center justify-between">
                    <div>
                      {siblings.prev && (
                        <a
                          href={`/store/${siblings.prev.slug || siblings.prev.id}`}
                          className="btn btn-outline"
                        >
                          ← Prev
                        </a>
                      )}
                    </div>
                    <div>
                      {siblings.next && (
                        <a
                          href={`/store/${siblings.next.slug || siblings.next.id}`}
                          className="btn btn-outline"
                        >
                          Next →
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Rating and Reviews */}
            <div className="mt-8">
              <div className="card p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Product Rating</h2>
                    <div className="flex items-center gap-3 mt-1">
                      {/* Star Rating */}
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <svg
                            key={star}
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill={star <= (p.rating || 4.5) ? "currentColor" : "none"}
                            className={`${
                              star <= (p.rating || 4.5) ? "text-yellow-400" : "text-gray-300"
                            }`}
                          >
                            <path
                              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                          </svg>
                        ))}
                      </div>
                      <span className="text-lg font-medium text-gray-900">
                        {p.rating || 4.5} out of 5
                      </span>
                      <span className="text-sm text-gray-500">
                        ({p.reviewCount || 8} review{(p.reviewCount || 8) !== 1 ? "s" : ""})
                      </span>
                    </div>
                  </div>
                </div>

                <ProductReviews productId={p.id} productName={p.name} />
              </div>
            </div>

            {/* Related products */}
            {(relLoading || related.length > 0) && (
              <div className="mt-8">
                <div className="text-lg font-semibold mb-3">Related products</div>
                {relLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="rounded-xl border bg-white animate-pulse">
                        <div className="aspect-square w-full rounded-t-xl bg-gray-100" />
                        <div className="p-3 space-y-2">
                          <div className="h-4 bg-gray-100 rounded w-3/4" />
                          <div className="h-3 bg-gray-100 rounded w-1/2" />
                          <div className="h-3 bg-gray-100 rounded w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : related.length === 0 ? (
                  <div className="text-ink/60 text-sm">No related products.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {related.map((rp) => (
                      <ProductCard
                        key={rp.id}
                        p={rp}
                        onClick={() => (window.location.href = `/store/${rp.slug || rp.id}`)}
                        showAdd
                        onAdd={(it) => {
                          const q = Math.max(0.25, Number(it.qty || 0.25));
                          addItem({ 
                            id: it.id, 
                            name: it.name, 
                            price: it.price, 
                            qty: q,
                            storeType: it.storeType,
                            storeId: it.storeId,
                          });
                        }}
                        showQuick
                        onQuick={(it) =>
                          (window.location.href = `/store/${it.slug || it.id}?quick=${encodeURIComponent(
                            it.slug || it.id
                          )}`)
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recently viewed */}
            {recent.length > 0 && (
              <div className="mt-10">
                <div className="text-lg font-semibold mb-3">Recently viewed</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {recent.map((rp) => (
                    <ProductCard
                      key={rp.id || rp.slug}
                      p={rp}
                      onClick={() => (window.location.href = `/store/${rp.slug || rp.id}`)}
                      showAdd
                      onAdd={(it) => {
                        const q = Math.max(0.25, Number(it.qty || 0.25));
                        addItem({ 
                          id: it.id, 
                          name: it.name, 
                          price: it.price, 
                          qty: q,
                          storeType: it.storeType,
                          storeId: it.storeId,
                        });
                      }}
                      showQuick
                      onQuick={(it) =>
                        (window.location.href = `/store/${it.slug || it.id}?quick=${encodeURIComponent(
                          it.slug || it.id
                        )}`)
                      }
                      showStock={false}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Sticky mobile action bar */}
            <div className="sm:hidden fixed inset-x-0 bottom-0 z-30 border-t border-border bg-white/90 backdrop-blur">
              <div className="mx-auto page-boxed page-gutter py-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] flex items-center gap-2 justify-between">
                <div className="inline-flex items-center gap-2">
                  <input
                    className="input h-9 w-20 text-center"
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(sanitizeNumericDot(e.target.value))}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      const min = 0.25;
                      
                      // Allow 0 as a valid input
                      if (isNaN(n) || n < 0) {
                        setQty('');
                      } else if (n > 0 && n < min) {
                        const rounded = Math.round(n * 4) / 4;
                        const clamped = stock == null
                          ? Math.max(min, rounded)
                          : Math.min(Math.max(min, rounded), Math.max(min, stock));
                        setQty(clamped);
                      } else {
                        const rounded = Math.round(n * 4) / 4;
                        const clamped = stock == null
                          ? Math.max(min, rounded)
                          : Math.min(Math.max(min, rounded), Math.max(min, stock));
                        setQty(clamped);
                      }
                    }}
                    inputMode="decimal"
                    enterKeyHint="done"
                  />
                  <button className="btn btn-outline h-9" onClick={addToCart} disabled={!canBuy || cooling}>
                    Add to cart
                  </button>
                </div>
                <button
                  className="btn btn-primary h-9"
                  onClick={() => {
                    const want = Math.max(0.25, Number(qty || 0.25));
                    addItem({
                      id: p.id,
                      slug: p.slug,
                      name: p.name,
                      price,
                      image: selectedImg || getFirstImage(p),
                      qty: want,
                      storeType: p.storeType,
                      storeId: p.storeId,
                    });
                    nav("/checkout");
                  }}
                  disabled={!canBuy}
                >
                  Buy now
                </button>
              </div>
            </div>

            {/* Image lightbox */}
            {zoomOpen && selectedImg && (
              <div
                className="fixed inset-0 z-40 flex items-center justify-center p-4"
                onClick={() => setZoomOpen(false)}
              >
                <div className="absolute inset-0 bg-black/50" />
                <div
                  className="relative max-w-5xl w-full max-h-[90vh] bg-white rounded-xl overflow-hidden transform transition-all duration-150 ease-out scale-95 opacity-0 animate-[fadeIn_.15s_ease-out_forwards,scaleIn_.15s_ease-out_forwards]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="absolute top-2 right-2 h-9 w-9 inline-flex items-center justify-center rounded hover:bg-gray-100"
                    onClick={() => setZoomOpen(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                  <div className="w-full h-full grid place-items-center bg-black">
                    {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                    <img src={selectedImg} alt={p.name + " zoomed image"} className="max-h-[90vh] object-contain" />
                  </div>
                </div>
              </div>
            )}

            {/* JSON-LD for SEO */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org/",
                  "@type": "Product",
                  name: p.name,
                  image: images.length ? images : getFirstImage(p) ? [getFirstImage(p)] : [],
                  sku: p.sku || undefined,
                  offers: {
                    "@type": "Offer",
                    priceCurrency: "PHP",
                    price: price,
                    availability:
                      Number(p.stock ?? 0) > 0
                        ? "https://schema.org/InStock"
                        : "https://schema.org/OutOfStock",
                    url: typeof window !== "undefined" ? window.location.href : undefined,
                  },
                }),
              }}
            />
          </div>
        </div>
      </PageBackground>
    </>
  );
}
